import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, TextField, FormControl, Select, MenuItem, FormHelperText, Button} from '@mui/material'
import {Controller} from 'react-hook-form'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {caregiverApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useParams} from 'react-router-dom'
import {parseError, ignoreTimezone} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {SkeletonContainer, FieldSkeleton, ButtonSkeleton} from '../../../../components'

const EmploymentInfoSkeleton = () => (
    <SkeletonContainer>
        <FieldSkeleton labelWidth={100} />
        <br />
        <FieldSkeleton labelWidth={100} />
        <br />
        <div className="form" style={{marginBottom: 0}}>
            <FieldSkeleton labelWidth={90} sx={{flex: 1}} />
            <FieldSkeleton labelWidth={90} sx={{flex: 1}} />
        </div>
        <br />
        <br />
        <div className="actions">
            <ButtonSkeleton width={80} />
            <ButtonSkeleton width={130} />
        </div>
    </SkeletonContainer>
)

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const [employmentInfoId, setEmploymentInfoId] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                employmentType: yup.string().required('Employment Type is required'),
                applicationDate: yup.date().required('Application Date is required'),
                referralSource: yup.string(),
                referralPerson: yup.string(),
            }),
        ),
    })

    const employmentTypes = [
        {key: 'full_time', value: 'Full-Time'},
        {key: 'part_time', value: 'Part-Time'},
    ]

    const referralSources = [
        {key: 'employee', value: 'Employee'},
        {key: 'other', value: 'Other'},
    ]

    useEffect(() => {
        reset(true)
    }, [])

    // The first load shows the skeleton; the Reset button refetches under the global spinner.
    const reset = (initial = false) => {
        if (!initial) setLoading(true)
        caregiverApi
            .getCaregiverEmploymentInfos(branchId, caregiverId)
            .then(({data}) => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const employmentInfo = data?.employment_info?.[0]
                setEmploymentInfoId(employmentInfo?.id)
                form.setValue('employmentType', employmentInfo?.employment_type)
                form.setValue('applicationDate', new Date(ignoreTimezone(employmentInfo?.application_date)))
                form.setValue('referralSource', employmentInfo?.referral_source)
                form.setValue('referralPerson', employmentInfo?.referral_person)
            })
            .catch(error => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const submit = async data => {
        try {
            setLoading(true)
            await caregiverApi.updateCaregiverEmploymentInfo(branchId, caregiverId, employmentInfoId, {
                employment_type: data?.employmentType,
                application_date: data?.applicationDate ? format(new Date(data?.applicationDate), 'yyyy-MM-dd') : '',
                referral_source: data?.referralSource,
                referral_person: data?.referralPerson,
            })

            setMessageBox({
                status: 'success',
                title: 'Caregiver Informations Updated',
                description: "Caregiver's Employment Information have been successfully updated. All changes are now reflected in the system.",
            })
            setLoading(false)
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <>
            {skeleton && <EmploymentInfoSkeleton />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Employment Type{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <FormControl fullWidth error={!!form.formState.errors['employmentType']}>
                    <Controller
                        {...form.register('employmentType')}
                        control={form.control}
                        render={({field}) => (
                            <Select
                                {...field}
                                value={field.value ?? ''}
                                onChange={e => field.onChange(e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                                displayEmpty
                                renderValue={selected => {
                                    if (selected === '') {
                                        return <span style={{color: '#aaa'}}>Select your employment type</span>
                                    }
                                    return employmentTypes.find(item => item.key === selected)?.value ?? selected
                                }}>
                                {employmentTypes.map(item => (
                                    <MenuItem key={item?.key} value={item?.key}>
                                        {item?.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    <FormHelperText>{form.formState.errors['employmentType']?.message}</FormHelperText>
                </FormControl>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Application Date{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <Controller
                    {...form.register('applicationDate')}
                    control={form.control}
                    render={({field}) => (
                        <DatePicker
                            {...field}
                            value={field.value ?? null}
                            onChange={newValue => field.onChange(newValue)}
                            enableAccessibleFieldDOMStructure={false}
                            slots={{
                                textField: params => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        error={!!form.formState.errors['applicationDate']}
                                        helperText={form.formState.errors['applicationDate']?.message}
                                    />
                                ),
                            }}
                        />
                    )}
                />
                <br />
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            Referral Source
                        </Typography>
                        <FormControl fullWidth>
                            <Controller
                                {...form.register('referralSource')}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        onChange={e => field.onChange(e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}
                                        displayEmpty
                                        renderValue={selected => {
                                            if (selected === '') {
                                                return <span style={{color: '#aaa'}}>Select your referral source</span>
                                            }
                                            return referralSources.find(item => item.key === selected)?.value ?? selected
                                        }}>
                                        {referralSources.map(item => (
                                            <MenuItem key={item?.key} value={item?.key}>
                                                {item?.value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            Referral Person
                        </Typography>
                        <TextField placeholder="Enter your referral person" fullWidth {...form.register('referralPerson')} />
                    </div>
                </div>
                <br />
                <br />
                <br />
                <div className="actions">
                    <Button variant="outlined" onClick={() => reset()}>
                        Reset
                    </Button>
                    <Button variant="contained" onClick={form.handleSubmit(submit)}>
                        Save Changes
                    </Button>
                </div>
            </div>
        </>
    )
}
